import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { SECTIONS } from '@aurea/contracts';
import { useAuth } from '../lib/auth';

/**
 * Переход между разделами системы.
 *
 * Права шире ролей: у администратора есть и доступ к встречам рекрутёра,
 * но попасть туда было неоткуда — в шапке админки просто не было ссылки.
 * Список строится по правам, поэтому и роль, собранная вручную, получает
 * ровно те разделы, что ей выдали.
 *
 * Тем, кому доступен один раздел, переключатель не показывается: выбирать
 * не из чего.
 */
export function SectionSwitcher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Клик мимо закрывает список — иначе он висит поверх страницы.
  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  if (!user) return null;

  const available = SECTIONS.filter((section) =>
    section.permissions.some((permission) => user.permissions.includes(permission)),
  );
  if (available.length < 2) return null;

  const current = available.find((section) => location.pathname.startsWith(section.path));

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-1.5 rounded-[9px] border border-border bg-surface-2 px-3 py-2 text-[12.5px] font-bold text-ink-dim hover:bg-border"
      >
        {current?.label ?? 'Разделы'}
        <span className={`text-[9px] transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>

      {open && (
        <div className="absolute top-[calc(100%+6px)] left-0 z-30 min-w-[200px] overflow-hidden rounded-[10px] border border-border bg-surface py-1 shadow-card">
          {available.map((section) => (
            <button
              key={section.path}
              onClick={() => {
                setOpen(false);
                void navigate(section.path);
              }}
              className={`block w-full px-3.5 py-2 text-left text-[13px] font-semibold hover:bg-surface-2 ${
                section === current ? 'text-accent' : 'text-ink-dim'
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
